/**
 * Questionnaires: the list, and the import.
 *
 * The import shows the mapping before it commits — which sheet, which column,
 * which row the questions start on, and the first few it found. A wrong guess
 * silently accepted becomes a questionnaire full of section headings, and by
 * then the customer has already spent model tokens on it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorMessage, type QuestionnaireSummary } from '../api';
import { extractQuestions, guessHeaderRow, guessQuestionColumn } from '../../core/questions';
import { readWorkbook, type LoadedWorkbook } from '../sheet';
import { link, navigate } from '../router';
import { Alert, Button, Dialog, EmptyState, Field, Input, PageHeader, Spinner, formatDate } from '../ui';
import {
  ArrowRightIcon,
  CheckIcon,
  FileSpreadsheetIcon,
  Icon,
  IconHover,
  Trash2Icon,
  UploadIcon,
} from '../icons';

function columnLabel(index: number): string {
  let label = '';
  let n = index;
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

function ImportDialog({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [workbook, setWorkbook] = useState<LoadedWorkbook | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [column, setColumn] = useState(0);
  const [firstRow, setFirstRow] = useState(1);
  const [customer, setCustomer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reused, setReused] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const sheet = workbook?.sheets[sheetIndex];
  const questions = sheet
    ? extractQuestions(sheet.rows, { questionColumn: column, firstDataRow: firstRow })
    : [];

  const width = sheet ? sheet.rows.reduce((max, r) => Math.max(max, r.length), 0) : 0;

  const pick = (index: number, book: LoadedWorkbook) => {
    const rows = book.sheets[index]?.rows ?? [];
    const header = guessHeaderRow(rows);
    setSheetIndex(index);
    setFirstRow(header + 1);
    setColumn(Math.max(guessQuestionColumn(rows, header), 0));
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const book = await readWorkbook(file);
      if (book.sheets.length === 0) {
        setError('That file has no readable sheets.');
        return;
      }
      setWorkbook(book);
      // Prefer the first sheet that actually looks like it holds questions.
      const best = book.sheets.findIndex(
        (s) => guessQuestionColumn(s.rows, guessHeaderRow(s.rows)) >= 0,
      );
      pick(best >= 0 ? best : 0, book);
      if (!customer) setCustomer(file.name.replace(/\.[^.]+$/, '').slice(0, 60));
    } catch {
      setError('Could not read that file. XLSX, XLS and CSV are supported.');
    }
  };

  /**
   * If this customer has sent a file before, start from the layout that worked
   * last time. Applied only when the sheet still exists and the column is still
   * within range — a remembered mapping pointed at a column that is not there is
   * worse than no mapping.
   */
  useEffect(() => {
    const name = customer.trim();
    if (!workbook || !name) return;

    let cancelled = false;
    void api
      .importHint(name)
      .then(({ hint }) => {
        if (cancelled || !hint) return;
        const index = workbook.sheets.findIndex((s) => s.name === hint.sheetName);
        const target = index >= 0 ? index : sheetIndex;
        const rows = workbook.sheets[target]?.rows ?? [];
        const width = rows.reduce((max, r) => Math.max(max, r.length), 0);
        if (hint.questionColumn < 0 || hint.questionColumn >= width) return;

        setSheetIndex(target);
        setColumn(hint.questionColumn);
        if (hint.firstRow >= 0) setFirstRow(hint.firstRow);
        setReused(true);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // Deliberately not re-running when the customer edits the mapping by hand:
    // it should suggest once, not fight them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook, customer]);

  const submit = async () => {
    if (!workbook || questions.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.createQuestionnaire({
        customer: customer.trim() || 'Untitled',
        filename: workbook.filename,
        // Kept so the next file from this customer starts with the layout
        // already filled in rather than guessed again.
        sheetName: sheet?.name ?? '',
        questionColumn: column,
        firstRow,
        questions: questions.map((q) => ({ rowIndex: q.rowIndex, text: q.text })),
      });
      onImported();
      navigate(`/questionnaires/${id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Import a questionnaire"
      description="Parsed in this browser. Only the question text is sent to the server."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={ArrowRightIcon}
            busy={busy}
            disabled={questions.length === 0 || !customer.trim()}
            onClick={submit}
          >
            Import {questions.length > 0 ? `${questions.length} questions` : ''}
          </Button>
        </>
      }
    >
      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {!workbook ? (
        <IconHover
          className="flex cursor-pointer flex-col items-center rounded-xl border border-dashed border-line-strong px-6 py-12 text-center"
          onClick={() => fileInput.current?.click()}
          onDragOver={(e: React.DragEvent) => e.preventDefault()}
          onDrop={(e: React.DragEvent) => {
            e.preventDefault();
            void onFile(e.dataTransfer.files[0]);
          }}
        >
          <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-sunken text-muted">
            <Icon icon={UploadIcon} size={18} />
          </span>
          <p className="text-[14px] font-medium">Drop the spreadsheet here</p>
          <p className="mt-1 text-[13px] text-muted">XLSX, XLS or CSV — it stays on this machine</p>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
        </IconHover>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg bg-sunken px-3 py-2 text-[13px]">
            <Icon icon={FileSpreadsheetIcon} size={15} isAnimated={false} />
            <span className="truncate">{workbook.filename}</span>
            <button
              className="ml-auto shrink-0 text-[12.5px] text-accent"
              onClick={() => setWorkbook(null)}
            >
              Change
            </button>
          </div>

          <Field label="Customer">
            <Input
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              placeholder="Northwind Corp"
            />
          </Field>

          {workbook.sheets.length > 1 ? (
            <Field label="Sheet">
              <select
                className="select"
                value={sheetIndex}
                onChange={(e) => pick(Number(e.target.value), workbook)}
              >
                {workbook.sheets.map((s, i) => (
                  <option key={s.name} value={i}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {reused ? (
            <p className="flex items-start gap-1.5 rounded-lg bg-accent-wash px-3 py-2 text-[12.5px] text-accent">
              <Icon icon={CheckIcon} size={13} isAnimated={false} />
              Using the same columns as the last file from this customer.
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Question column">
              <select
                className="select"
                value={column}
                onChange={(e) => setColumn(Number(e.target.value))}
              >
                {Array.from({ length: width }, (_, i) => {
                  const header = sheet?.rows[Math.max(firstRow - 1, 0)]?.[i]?.trim();
                  return (
                    <option key={i} value={i}>
                      {columnLabel(i)}
                      {header ? ` — ${header.slice(0, 30)}` : ''}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="First question on row">
              <Input
                type="number"
                min={1}
                value={firstRow + 1}
                onChange={(e) => setFirstRow(Math.max(Number(e.target.value) - 1, 0))}
              />
            </Field>
          </div>

          <div>
            <p className="label">
              Found {questions.length} {questions.length === 1 ? 'question' : 'questions'}
            </p>
            <div className="max-h-52 overflow-y-auto rounded-lg border border-line">
              {questions.length === 0 ? (
                <p className="px-3 py-6 text-center text-[13px] text-faint">
                  Nothing in that column looks like a question. Try another column.
                </p>
              ) : (
                questions.slice(0, 40).map((q) => (
                  <div
                    key={q.rowIndex}
                    className="flex gap-3 border-b border-line px-3 py-2 text-[13px] last:border-b-0"
                  >
                    <span className="citation shrink-0 text-faint">{q.rowIndex + 1}</span>
                    <span className="text-muted">{q.text}</span>
                  </div>
                ))
              )}
            </div>
            {questions.length > 40 ? (
              <p className="mt-1.5 text-[12.5px] text-faint">
                Showing the first 40 of {questions.length}.
              </p>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}

export function QuestionnairesView() {
  const [items, setItems] = useState<QuestionnaireSummary[] | null>(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const { questionnaires } = await api.listQuestionnaires().catch(() => ({ questionnaires: [] }));
    setItems(questionnaires);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (item: QuestionnaireSummary) => {
    if (!confirm(`Delete the questionnaire from ${item.customer}?`)) return;
    await api.deleteQuestionnaire(item.id).catch(() => undefined);
    await load();
  };

  return (
    <>
      <PageHeader
        title="Questionnaires"
        description="Import the spreadsheet a customer sent, draft against your evidence, and export it filled in."
        actions={
          <Button variant="primary" icon={UploadIcon} onClick={() => setImporting(true)}>
            Import
          </Button>
        }
      />

      {items === null ? (
        <div className="flex justify-center py-16">
          <Spinner size={20} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={FileSpreadsheetIcon}
          title="No questionnaires yet"
          action={
            <Button variant="primary" icon={UploadIcon} onClick={() => setImporting(true)}>
              Import a spreadsheet
            </Button>
          }
        >
          Drop in the XLSX a customer sent. It is parsed here in your browser — only the question
          text reaches the server, and only after you have confirmed the column it came from.
        </EmptyState>
      ) : (
        <div className="space-y-2.5">
          {items.map((item) => {
            const done = item.answered + item.unsupported;
            const pct = item.total > 0 ? Math.round((item.answered / item.total) * 100) : 0;
            return (
              <IconHover key={item.id} className="card p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <a {...link(`/questionnaires/${item.id}`)} className="min-w-0 flex-1">
                    <h3 className="truncate text-[15px] font-medium">{item.customer}</h3>
                    <p className="mt-0.5 truncate text-[12.5px] text-faint">
                      {item.filename} · imported {formatDate(item.createdAt)}
                    </p>
                  </a>

                  <div className="flex items-center gap-2">
                    {item.answered > 0 ? (
                      <span className="chip chip-accent">{item.answered} answered</span>
                    ) : null}
                    {item.unsupported > 0 ? (
                      <span className="chip chip-refusal">{item.unsupported} unsupported</span>
                    ) : null}
                    {done < item.total ? (
                      <span className="chip chip-neutral">{item.total - done} to draft</span>
                    ) : null}
                  </div>

                  <Button small variant="ghost" icon={Trash2Icon} onClick={() => remove(item)} />
                </div>

                <div className="mt-3 h-1 overflow-hidden rounded-full bg-sunken">
                  <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
                </div>
              </IconHover>
            );
          })}
        </div>
      )}

      {importing ? (
        <ImportDialog
          onClose={() => setImporting(false)}
          onImported={() => {
            setImporting(false);
            void load();
          }}
        />
      ) : null}
    </>
  );
}
